import React, { useCallback, useMemo } from 'react';
import { useHistory } from "react-router-dom";

import {
	Button,
	IconButton,
	MeetingStatus,
	Space,
} from '@components';

import {
	MeetingActions,
	MeetingInfo, MeetingStatuses, UpdateMeetingInfoRequest,
} from '@api';

import {
	makeMeetingDateString,
	useModal,
	UserRights,
} from '@common';

import {
	routes,
} from '@routes';

import {
	SettingsIcon,
	InfoIcon,
	ArrowLeftIcon,
} from '@icons';

import './MeetingPageHeader.scss';
import { EditMeetingInfoModal, ViewMeetingInfoModal } from '@modals';
import { useUserStoreContext } from '@store';

interface MeetingPageHeaderProps {
	meetingInfo: MeetingInfo,
	onUpdateInfo: (updatedMeetingInfo: UpdateMeetingInfoRequest) => void,
	onStatusAction: (action: MeetingActions) => void,
}

function MeetingPageHeader(props: MeetingPageHeaderProps): JSX.Element {
	const { 
			meetingInfo,
			onUpdateInfo,
			onStatusAction,
	} = props;

	const { state: UserStoreState } = useUserStoreContext();

	const history = useHistory();

	const onMeetingInfoUpdate = useCallback(async (updatedMeetingInfo: UpdateMeetingInfoRequest):Promise<void> => {
		onUpdateInfo(updatedMeetingInfo);
	}, [onUpdateInfo]);

	const {
		isModalOpen: isEditMeetingInfoModalOpen,
		closeModal: closeEditMeetingInfoModal,
		openModal: openEditMeetingInfoModal,
	} = useModal();

	const {
		isModalOpen: isViewMeetingInfoModalOpen,
		closeModal: closeViewMeetingInfoModal,
		openModal: openViewMeetingInfoModal,
	} = useModal();

	const onEditMeetingInfo = useCallback(() => {
		openEditMeetingInfoModal();
	}, [openEditMeetingInfoModal]);

	const onViewMeetingInfo = useCallback(() => {
		openViewMeetingInfoModal();
	}, [openViewMeetingInfoModal]);


	const editMeetingInfoModalView = useMemo((): JSX.Element => {
		return isEditMeetingInfoModalOpen && (
			<EditMeetingInfoModal
				closeModal={closeEditMeetingInfoModal}
				meetingInfo={meetingInfo}
				onSubmit={onMeetingInfoUpdate}
			/>
		);
	}, [isEditMeetingInfoModalOpen, meetingInfo, onMeetingInfoUpdate, closeEditMeetingInfoModal]);

	const viewMeetingInfoModalView = useMemo((): JSX.Element => {
		return isViewMeetingInfoModalOpen && (
			<ViewMeetingInfoModal
				closeModal={closeViewMeetingInfoModal}
				meetingInfo={meetingInfo}
			/>
		);
	}, [isViewMeetingInfoModalOpen, meetingInfo, closeViewMeetingInfoModal]);
	
	const goBack = useCallback(() => {
		history.push(routes.index.path);
	}, [history]);

	const headerContent = useMemo((): JSX.Element => {
		let meetingLocation = "";
		if (meetingInfo.locations) {
			meetingLocation = meetingInfo.locations.map(loc => loc.title).join(", ");
		}
		const
			titleText = meetingInfo.subject || "",
			subtitleText = `${meetingInfo.startUtc && makeMeetingDateString(meetingInfo.startUtc, meetingInfo.endUtc)} ${meetingLocation}`;

		return (
			<div className="meeting-header">
				<div className='meeting-header__lead'>
					<IconButton
						theme="default"
						onClick={goBack}
						title="Вернуться на главную"
						iconSize='24px'
						size="simmetric-smaller"
						icon={<ArrowLeftIcon />}
						/>
					
				</div>
				<div className='meeting-header__main'>
					<div className="meeting-header__title" title={titleText}>
						{titleText}
					</div>
					<div className="meeting-header__subtitle">
						<span className='meeting-header__subtitle-text'>{subtitleText}</span>
						<MeetingStatus status={meetingInfo.status}  />
					</div>
				</div>
			</div>
		);
	}, [meetingInfo, goBack]);

	const headerTrailing = useMemo((): JSX.Element => {
		const hasEditingRights =
			UserStoreState.rights.includes(UserRights.edit_meeting_info) 
		|| (UserStoreState.rights.includes(UserRights.edit_own_meeting_info) && meetingInfo.isOwn)
		;

		const canEditMeetingInfo = meetingInfo &&  meetingInfo.status && 
			(meetingInfo.status.alias == MeetingStatuses.InWork || meetingInfo.status.alias == MeetingStatuses.New || meetingInfo.status.alias == MeetingStatuses.Plan)
			&& hasEditingRights;

		const actionButtons = meetingInfo &&  meetingInfo.status && hasEditingRights && 
			meetingInfo.stateMachine.actions.map(action => {
			let actionName: string = action;
			switch (action) {
				case MeetingActions.ProcessingComplete:
					actionName = "Завершить обработку";
					break;

				case MeetingActions.TakeToWork:
					actionName = "Взять в работу";
					break;

				case MeetingActions.StartRecording:
					actionName = "Начать запись";
					break;

				case MeetingActions.StopRecording:
					actionName = "Остановить запись";
					break;

				case MeetingActions.Complete:
					actionName = "Завершить работу";
					break;

				case MeetingActions.ReturnToWork:
					actionName = "Вернуть в работу";
					break;
			}

			return <Button key={action} onClick={() => onStatusAction(action)}>{actionName}</Button>
		});

		return (
			<div className="meeting-header__trailing">
				<Space className='meeting-header__centered'>
					<Space className='meeting-header__actions'>
						{actionButtons}
					</Space>
					<div className='info'>
						{(canEditMeetingInfo 
						?<IconButton
							icon={<SettingsIcon />}
							iconSize="30px"
							size="nogap"
							title='Редактирование собрания'
							onClick={onEditMeetingInfo}
							disabled={!meetingInfo}
						/>
						:<IconButton
							icon={<InfoIcon />}
							iconSize="30px"
							size="nogap"
							title='Информация о собрании'
							onClick={onViewMeetingInfo}
							disabled={!meetingInfo}
						/>
						)}
					</div>
				</Space>
			</div>
		);
	}, [meetingInfo, onEditMeetingInfo, onViewMeetingInfo, onStatusAction, UserStoreState]);

	return (
		<div className='meeting-header__block-layout'>
			<div className='meeting-header__block-layout-main'>
				{headerContent}
			</div>
			<div className='meeting-header__block-layout-tail'>
				{headerTrailing}
			</div>
			{editMeetingInfoModalView}
			{viewMeetingInfoModalView}
		</div>
	);
}

export {MeetingPageHeader};